import React, { useCallback, useEffect, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import _isEqual from 'lodash/isEqual';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { useGetCustomerStatementHistoryLazyQuery } from '@trqk/api';
import {
    leftNavConstants,
    IconInfoCircle,
    QuarterlyRunRate,
} from '@trqk/components';
import { locale, mapQRRHistoryToChartDetails } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { FULL_PATHS, HASHES } from '../../../../../constants';
import { useLogData, usePaymentPlanStatus } from '../../../../../hooks';
import { heights, totalRoyaltiesSizes } from '../../constants';

import './total-royalties.css';

const { WIDTHS } = leftNavConstants;

const { GET_SESSION_CURRENT_USER } = queryStrings;

const GET_TRQK_USER = gql`
    query getTrqkUser($id: ID!) {
        getTrqkUser(id: $id) {
            id
            subscription_royalty_capacity
        }
    }
`;

const TOTAL_ROYALTIES_HANDLER_KEY = 'totalRoyalties';

interface TotalRoyaltiesProps {
    /**
     * Timestamp for when this tab was last opened; if this changes, it means we should refresh
     * the data.
     */
    refresh: number;
    /**
     * Element ID for this section of the Reports page (corresponds to a URL hash)
     */
    sectionId: string;
    /**
     * A method for setting the loading state of children in the parent Reports component
     */
    setLoadingStates: SetState<Record<string, boolean>>;
}

/**
 * Renders a line chart showing the total earnings for each statement, with one line per entity.
 * Also shows a table which explains how the most recent statement earnings for each entity are
 * summed to calculate the quarterly run rate for billing purposes.
 * @param props see {@link TotalRoyaltiesProps}
 * @returns
 */
export const TotalRoyalties = ({
    refresh,
    sectionId,
    setLoadingStates,
}: TotalRoyaltiesProps): JSX.Element => {
    const [chartDetails, setChartDetails] =
        useState<QRRHistoryChartDetails | null>(null);

    const [loading, setLoading] = useState(true);
    const { addHandler, deleteHandlers } = useLogData();
    const { isPublisherEdition } = usePaymentPlanStatus();

    const [getCustomerStatementHistory] =
        useGetCustomerStatementHistoryLazyQuery({
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: (data): void => {
                const historyData =
                    data?.getCustomerStatementHistory?.data ?? null;

                if (historyData !== null) {
                    const nextChartDetails =
                        mapQRRHistoryToChartDetails(historyData);

                    if (!_isEqual(nextChartDetails, chartDetails)) {
                        setLoading(true);

                        setChartDetails(nextChartDetails);

                        setTimeout(() => {
                            setLoading(false);
                        }, 1000);
                    }
                }
            },
        });

    const [getTrqkUser, getTrqkUserQuery] = useLazyQuery<{
        getTrqkUser: TrqkUser;
    }>(GET_TRQK_USER, {
        fetchPolicy: 'network-only',
    });

    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    useEffect(() => {
        if (userId) {
            getCustomerStatementHistory({
                variables: {
                    user_id: userId,
                },
            });

            getTrqkUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [userId]);

    useEffect(() => {
        if (refresh > 0 && userId) {
            getCustomerStatementHistory({
                variables: {
                    user_id: userId,
                },
            });
        }
    }, [refresh, userId]);

    const fetchTrqkUserOnSuccess = useCallback(
        (event: LogEvent) => {
            if (userId && event.eventStatus === 'success') {
                getTrqkUser({
                    variables: {
                        id: userId,
                    },
                });
            }
        },
        [userId],
    );

    const fetchCustomerStatementHistoryOnSuccess = useCallback(
        (event: LogEvent) => {
            if (userId && event.eventStatus === 'success') {
                getCustomerStatementHistory({
                    variables: {
                        user_id: userId,
                    },
                });
            }
        },
        [userId],
    );

    // Add event handlers for the log events that relate to Chargebee subscription changes
    useEffect(() => {
        addHandler({
            eventTypes: ['entityRefreshed', 'entityValidation'],
            handler: fetchCustomerStatementHistoryOnSuccess,
            handlerKey: TOTAL_ROYALTIES_HANDLER_KEY,
        });

        addHandler({
            eventTypes: [
                'chargebeeAdminOverride',
                'chargebeeSubscriptionCreated',
                'chargebeeSubscriptionChanged',
                'chargebeeSubscriptionCancelled',
                'chargebeeSubscriptionReactivated',
            ],
            handler: fetchTrqkUserOnSuccess,
            handlerKey: TOTAL_ROYALTIES_HANDLER_KEY,
        });

        return () => {
            // When component unmounts, remove its log event handlers
            deleteHandlers(TOTAL_ROYALTIES_HANDLER_KEY);
        };
    }, [fetchCustomerStatementHistoryOnSuccess, fetchTrqkUserOnSuccess]);

    const chartData = chartDetails?.chartData ?? null;
    const tableData = chartDetails?.tableData ?? null;
    const seriesConfigs = chartDetails?.seriesConfigs ?? null;
    const royaltyCapacity =
        getTrqkUserQuery?.data?.getTrqkUser?.subscription_royalty_capacity ??
        null;

    const sectionIsLoading =
        chartData === null ||
        tableData === null ||
        seriesConfigs === null ||
        loading;

    const showEmptyState =
        !sectionIsLoading && chartData !== null && chartData.length === 0;

    useEffect(() => {
        if (!sectionIsLoading) {
            setLoadingStates(
                (prev: Record<string, boolean>): Record<string, boolean> => ({
                    ...prev,
                    [sectionId]: false,
                }),
            );
        }
    }, [sectionIsLoading]);

    return (
        <div
            className={cx('total-royalties bg-white rounded-lg py-8', {
                'w-full': !showEmptyState && !sectionIsLoading,
            })}
            style={{
                width:
                    showEmptyState || sectionIsLoading
                        ? WIDTHS.MAIN_XL
                        : undefined,
            }}
        >
            <h4 className="ml-10 mb-6" data-cy="total-royalties-title">
                {locale.enUS.REPORTS.SECTIONS.TOTAL_ROYALTIES.SECTION_TITLE}
            </h4>
            {showEmptyState && (
                <div
                    style={{ height: heights.minSectionHeight }}
                    className="flex justify-center items-center text-tertiary-400 italic"
                >
                    <div className="text-center">
                        <IconInfoCircle
                            fillColor="tertiary-400"
                            addClasses="mr-1"
                        />
                        {
                            locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES
                                .DATA_IS_EMPTY.AFTER_YOU
                        }{' '}
                        <Link
                            className="underline"
                            to={({ search }) => ({
                                hash: HASHES.MANAGE_STATEMENTS.UPLOADS,
                                pathname: FULL_PATHS.MANAGE_STATEMENTS_CHARTS,
                                search,
                            })}
                        >
                            {
                                locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES
                                    .DATA_IS_EMPTY.UPLOAD_STATEMENTS
                            }
                        </Link>{' '}
                        {
                            locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES
                                .DATA_IS_EMPTY.YOU_WILL_SEE
                        }
                    </div>
                </div>
            )}
            {chartData !== null &&
                chartData.length > 0 &&
                seriesConfigs !== null &&
                !loading && (
                    <QuarterlyRunRate
                        height={totalRoyaltiesSizes.table.height}
                        data={chartData}
                        royaltyCapacity={royaltyCapacity}
                        seriesConfigs={seriesConfigs}
                        numberFormat="$#.00a"
                        unlimitedRoyalties={isPublisherEdition === true}
                    />
                )}
        </div>
    );
};
